import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { AssetFormModal } from '../features/iso27001/assets/AssetFormModal';
import {
  createInfoAsset,
  fetchInfoAsset,
  fetchInfoAssets,
  updateInfoAsset,
} from '../features/iso27001/assets/assetsApi';
import type { AssetClassification, InfoAssetSummary, InfoAssetType } from '../features/iso27001/assets/types';
import {
  ASSET_CLASSIFICATION_LABEL,
  INFO_ASSET_TYPE_LABEL,
  classificationBadgeClass,
  formatCia,
} from '../features/iso27001/assets/assetsUtils';

const TYPES = Object.keys(INFO_ASSET_TYPE_LABEL) as InfoAssetType[];
const CLASSIFICATIONS = Object.keys(ASSET_CLASSIFICATION_LABEL) as AssetClassification[];

export function InfoAssetsPage() {
  const { canEdit } = usePlatformAccessLevel('iso27001.assets.access');
  const [items, setItems] = useState<InfoAssetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterClassification, setFilterClassification] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<InfoAssetSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchInfoAssets({
        page,
        pageSize: 20,
        type: filterType || undefined,
        classification: filterClassification || undefined,
        search: search.trim() || undefined,
      });
      setItems(list.data);
      setTotalPages(list.pagination.pages);
    } catch {
      setError('No se pudieron cargar los activos de información.');
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterClassification, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = async (item: InfoAssetSummary) => {
    try {
      const detail = await fetchInfoAsset(item.id);
      setEditingAsset(detail);
      setModalOpen(true);
    } catch {
      setError('No se pudo cargar el activo para editar.');
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAsset(null);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Inventario de activos</h1>
          <p className="text-muted-foreground text-sm">ISO 27001 — A.5.9 y análisis CIA</p>
        </div>
        {canEdit && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setEditingAsset(null);
              setModalOpen(true);
            }}
          >
            Nuevo activo
          </button>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <select
          className="input w-full"
          value={filterType}
          onChange={(e) => {
            setPage(1);
            setFilterType(e.target.value);
          }}
        >
          <option value="">Todos los tipos</option>
          {TYPES.map((type) => (
            <option key={type} value={type}>
              {INFO_ASSET_TYPE_LABEL[type]}
            </option>
          ))}
        </select>
        <select
          className="input w-full"
          value={filterClassification}
          onChange={(e) => {
            setPage(1);
            setFilterClassification(e.target.value);
          }}
        >
          <option value="">Todas las clasificaciones</option>
          {CLASSIFICATIONS.map((cls) => (
            <option key={cls} value={cls}>
              {ASSET_CLASSIFICATION_LABEL[cls]}
            </option>
          ))}
        </select>
        <input
          className="input w-full"
          placeholder="Buscar…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Código</th>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Clasificación</th>
              <th className="px-3 py-2 text-left">CIA</th>
              <th className="px-3 py-2 text-left">Propietario</th>
              <th className="px-3 py-2 text-left">Riesgos</th>
              {canEdit ? <th className="px-3 py-2 text-left">Acciones</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-3 py-6 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-3 py-6 text-center text-muted-foreground">
                  Sin activos registrados.
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const riskCount = item._count?.linkedRisks ?? 0;
                return (
                  <tr key={item.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono">{item.code}</td>
                    <td className="px-3 py-2">{item.name}</td>
                    <td className="px-3 py-2">{INFO_ASSET_TYPE_LABEL[item.type]}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs ${classificationBadgeClass(item.classification)}`}
                      >
                        {ASSET_CLASSIFICATION_LABEL[item.classification]}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {formatCia(item.ciaConfidentiality, item.ciaIntegrity, item.ciaAvailability)}
                    </td>
                    <td className="px-3 py-2">{item.owner}</td>
                    <td className="px-3 py-2">
                      {riskCount > 0 ? (
                        <Link
                          to="/riesgos"
                          className="text-primary underline-offset-4 hover:underline"
                          title="Ver riesgos vinculados en el registro"
                        >
                          {riskCount}
                        </Link>
                      ) : (
                        '0'
                      )}
                    </td>
                    {canEdit ? (
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => void openEdit(item)}
                        >
                          Editar
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <AssetFormModal
        open={modalOpen}
        title={editingAsset ? 'Editar activo de información' : 'Nuevo activo de información'}
        initial={editingAsset ?? undefined}
        onClose={closeModal}
        onSubmit={async (payload) => {
          if (editingAsset) {
            await updateInfoAsset(editingAsset.id, payload);
          } else {
            await createInfoAsset(payload);
          }
          await load();
        }}
      />
    </div>
  );
}
